import { Component, ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { captureError } from "@/lib/sentry";
import { isChunkLoadError } from "@/lib/lazyWithRetry";

const RELOAD_KEY = "bp.chunk-reload";

interface State {
  error: Error | null;
  componentStack?: string;
}

interface Props {
  children: ReactNode;
  fallback?: (err: Error, reset: () => void) => ReactNode;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: { componentStack?: string | null }) {
    this.setState({ componentStack: info.componentStack ?? undefined });

    // Stale-chunk recovery: after a redeploy, hashed JS files referenced by
    // the cached HTML may 404. Auto-reload once to pick up fresh HTML.
    if (isChunkLoadError(error)) {
      try {
        const already = sessionStorage.getItem(RELOAD_KEY);
        if (!already) {
          sessionStorage.setItem(RELOAD_KEY, "1");
          window.location.reload();
          return;
        }
      } catch {
        /* noop */
      }
      // Don't pollute error logs with deploy-artifact noise.
      return;
    }

    captureError(error, { componentStack: info.componentStack ?? null });
    // fire and forget
    void supabase.functions.invoke("report-client-error", {
      body: {
        message: error.message,
        stack: error.stack ?? null,
        url: typeof window !== "undefined" ? window.location.href : null,
        user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
        component_stack: info.componentStack ?? null,
      },
    });
  }

  reset = () => this.setState({ error: null, componentStack: undefined });

  render() {
    if (this.state.error) {
      if (this.props.fallback) return this.props.fallback(this.state.error, this.reset);
      const isChunk = isChunkLoadError(this.state.error);
      return (
        <div role="alert" className="min-h-[60vh] flex items-center justify-center p-6">
          <div className="max-w-md text-center space-y-4">
            <h1 className="text-2xl font-semibold">
              {isChunk ? "A new version is available" : "Something went wrong"}
            </h1>
            <p className="text-muted-foreground text-sm">
              {isChunk
                ? "We just shipped an update. Please refresh to load the latest version."
                : "We've been notified and are looking into it. You can try reloading the page."}
            </p>
            <div className="flex gap-2 justify-center">
              <Button
                onClick={() => {
                  try { sessionStorage.removeItem(RELOAD_KEY); } catch { /* noop */ }
                  window.location.reload();
                }}
              >
                {isChunk ? "Refresh" : "Reload"}
              </Button>
              <Button variant="outline" onClick={this.reset}>Dismiss</Button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
